import { FC, useMemo } from 'react';

import { XIcon } from '@heroicons/react/outline';
import {
  GiftCouponSummary,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { ShareChannelIcon } from '@service/giftStore/components/ShareChannelIcon';

import { fmtTime, shareChannelLabel } from './constants';

interface CouponShareHistoryModalProps {
  coupon: GiftCouponSummary;
  onClose: () => void;
}

interface NormalizedEntry {
  sharedAt: Date | string;
  unsharedAt?: Date | string;
  shareChannel?: ShareChannel;
}

/**
 * 한 쿠폰의 재공유 이력(`shareHistory`) 을 보여주는 모달.
 * - `KakaoShareCallbackModal` 과 시각적으로 동일한 톤.
 * - API 응답 순서와 무관하게 `sharedAt` 오래된 → 최신 순으로 정렬해 타임라인처럼 보여준다.
 * - 가장 최근 항목은 "최신" 뱃지로 강조.
 */
export const CouponShareHistoryModal: FC<CouponShareHistoryModalProps> = ({
  coupon,
  onClose,
}) => {
  const sortedHistory = useMemo<NormalizedEntry[]>(() => {
    const history = (coupon.shareHistory ?? []) as NormalizedEntry[];
    return [...history].sort(
      (a, b) => new Date(a.sharedAt).getTime() - new Date(b.sharedAt).getTime(),
    );
  }, [coupon.shareHistory]);

  const total = sortedHistory.length;
  const lastIdx = total - 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between px-5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-bold text-gray-900">재공유 이력</h3>
            <span className="text-xs text-gray-500">
              <span className="font-mono">
                {coupon.code}-{coupon.seq}
              </span>
              <span className="mx-1 text-gray-300">·</span>
              <span className="font-semibold text-indigo-700">총 {total}회 공유</span>
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
          {total === 0 ? (
            <div className="flex h-32 items-center justify-center text-sm text-gray-400">
              재공유 이력이 없습니다.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2 font-medium">순번</th>
                    <th className="whitespace-nowrap px-3 py-2 font-medium">공유 시각</th>
                    <th className="whitespace-nowrap px-3 py-2 font-medium">
                      공유 해제 시각
                    </th>
                    <th className="whitespace-nowrap px-3 py-2 font-medium">채널</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {sortedHistory.map((h, i) => {
                    const isLatest = i === lastIdx;
                    const channel = h.shareChannel;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">
                          <span className="inline-flex items-center gap-1">
                            <span>{i + 1}</span>
                            {isLatest && (
                              <span className="inline-flex rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-700">
                                최신
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5 text-indigo-700">
                          {fmtTime(h.sharedAt)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5">
                          {h.unsharedAt ? (
                            <span className="text-red-500">
                              {fmtTime(h.unsharedAt)}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5">
                          {channel ? (
                            <span className="inline-flex items-center gap-1 text-gray-700">
                              <ShareChannelIcon
                                channel={channel}
                                className="h-3.5 w-3.5"
                              />
                              <span>{shareChannelLabel[channel] ?? channel}</span>
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 && (
            <p className="pt-2 text-[11px] text-gray-400">
              * `공유 시각` 오래된 순으로 정렬되어 있습니다. 가장 마지막 항목이 현재
              유효한 공유입니다.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
